/**
 * Bundle Set Module
 * 
 * Handles bundle product interaction:
 * - Calculates and updates running total
 * - Handles variant selection
 * - Handles bundle quantity multiplier
 * - Submits all items to cart
 */

(function () {
    'use strict';

    // ─── Constants ────────────────────────────────────────────────────────────

    const SELECTORS = {
        dataBlock: '#bundle-set-data',
        mandatoryItem: '[data-mandatory-item]',
        variantSelector: '[data-variant-selector]',
        bundleQty: '[data-bundle-qty]',
        runningTotal: '[data-bundle-total]',
        runningTotalCompare: '[data-bundle-total-compare]',
        atcButton: '[data-bundle-atc]',
        checkoutButton: '[data-bundle-checkout]',
        errorContainer: '[data-bundle-error]',
        optionalItem: '[data-optional-item]',
        optionalQty: '[data-optional-qty]',
        quantityButton: '.quantity-selector__button',
    };

    // ─── Utilities ────────────────────────────────────────────────────────────

    function formatMoney(cents) {
        // Use Shopify's money formatter if available
        if (window.Shopify && window.Shopify.formatMoney) {
            return window.Shopify.formatMoney(cents, window.themeVariables.moneyFormat || '${{amount}}');
        }
        // Fallback formatter
        const dollars = (cents / 100).toFixed(2);
        return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    async function addItemsToCart(items) {
        const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items, sections: ['mini-cart'] }),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.description || 'Could not add items to cart.');
        }

        return data;
    }

    async function notifyCartUpdated(addResponse) {
        const cartType = window.themeVariables?.settings?.cartType;
        const cartUrl = window.themeVariables?.routes?.cartUrl || '/cart';

        if (cartType === 'page') {
            window.location.href = cartUrl;
            return;
        }

        if (cartType === 'drawer') {
            // Fetch fresh cart state and open the drawer
            try {
                const cartResponse = await fetch(cartUrl + '.js');
                const cartContent = await cartResponse.json();
                cartContent['sections'] = addResponse.sections || {};

                document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
                    bubbles: true,
                    detail: { cart: cartContent },
                }));

                document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                    bubbles: true,
                    detail: {
                        cart: cartContent,
                        openMiniCart: true,
                    },
                }));
            } catch (e) {
                console.warn('[BundleSet] Could not refresh cart:', e);
            }
            return;
        }

        // cartType === 'message' (or fallback): show the notification banner
        try {
            const cartResponse = await fetch(cartUrl + '.js');
            const cartContent = await cartResponse.json();
            cartContent['sections'] = addResponse.sections || {};

            document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
                bubbles: true,
                detail: { cart: cartContent },
            }));

            // Re-render the drawer in the background (without opening it)
            // so it shows current contents if the user opens it manually
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true,
                detail: { cart: cartContent, openMiniCart: false },
            }));
        } catch (e) {
            console.warn('[BundleSet] Could not refresh cart count:', e);
        }

        document.documentElement.dispatchEvent(new CustomEvent('cart-notification:show', {
            bubbles: true,
            detail: { status: 'success', error: '' },
        }));
    }

    // ─── BundleSet class ──────────────────────────────────────────────────────

    class BundleSet {
        constructor() {
            const dataEl = document.querySelector(SELECTORS.dataBlock);
            if (!dataEl) return; // Not a bundle page

            this.data = JSON.parse(dataEl.textContent);

            this.totalEl = document.querySelector(SELECTORS.runningTotal);
            this.totalCompareEl = document.querySelector(SELECTORS.runningTotalCompare);
            this.bundleQtyInput = document.querySelector(SELECTORS.bundleQty);
            this.atcButton = document.querySelector(SELECTORS.atcButton);
            this.checkoutButton = document.querySelector(SELECTORS.checkoutButton);
            this.errorEl = document.querySelector(SELECTORS.errorContainer);

            this._init();
        }

        _init() {
            this._bindEvents();
            this._updateTotal();
            this._updateItemPrices();
            this._updateItemImages();
            this._updateItemLinks();
            this._syncMainGalleryFromPrimaryItemWithRetry();
        }

        _bindEvents() {
            // Variant selectors
            document.querySelectorAll(SELECTORS.variantSelector).forEach((el) => {
                if (el.tagName === 'SELECT') {
                    el.addEventListener('change', () => this._onVariantChange(el));
                }
            });

            // Bundle quantity
            this._bindQuantityInput(this.bundleQtyInput);

            // Optional item quantities
            document.querySelectorAll(SELECTORS.optionalQty).forEach((input) => {
                this._bindQuantityInput(input);
            });

            // Add to cart
            if (this.atcButton) {
                this.atcButton.addEventListener('click', () => this._onAddToCart());
            }

            // Checkout
            if (this.checkoutButton) {
                this.checkoutButton.addEventListener('click', () => this._onCheckout());
            }
        }

        _bindQuantityInput(inputEl) {
            if (!inputEl) return;

            inputEl.addEventListener('change', () => this._updateTotal());
            inputEl.addEventListener('input', () => this._updateTotal());
            inputEl.addEventListener('keydown', (event) => {
                if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                    this._updateTotal();
                }
            });

            const quantitySelector = inputEl.closest('quantity-selector');
            if (!quantitySelector) return;

            quantitySelector.querySelectorAll(SELECTORS.quantityButton).forEach((button) => {
                button.addEventListener('click', () => {
                    requestAnimationFrame(() => this._updateTotal());
                });
            });
        }

        _onVariantChange(selectEl) {
            const card = selectEl.closest(SELECTORS.optionalItem) || selectEl.closest(SELECTORS.mandatoryItem);
            if (card) {
                this._updateItemPrice(card);
                this._updateItemImage(card);
                this._updateItemLink(card);
                this._syncMainGalleryFromItem(card);
            }
            this._updateTotal();
        }

        _buildItemPriceHtml(price, comparePrice) {
            if (comparePrice > price && comparePrice > 0) {
                return '<span class="price price--compare">' + formatMoney(comparePrice) + '</span>' +
                    '<span class="price price--highlight">' + formatMoney(price) + '</span>';
            }
            return '<span class="price">' + formatMoney(price) + '</span>';
        }

        _updateItemPrice(card) {
            const priceEl = card.querySelector('[data-item-price]');
            if (!priceEl) return;
            const price = this._getSelectedVariantPrice(card);
            const comparePrice = this._getSelectedVariantComparePrice(card);
            priceEl.innerHTML = this._buildItemPriceHtml(price, comparePrice);
        }

        _updateItemPrices() {
            document.querySelectorAll(SELECTORS.optionalItem).forEach((card) => this._updateItemPrice(card));
        }

        _updateItemImage(card) {
            const imageEl = card.querySelector('.bundle-set__item-image img');
            if (!imageEl) return;

            const selector = card.querySelector(SELECTORS.variantSelector);
            if (!selector) return;

            let imageSrc = '';
            let imageAlt = '';

            if (selector.tagName === 'SELECT') {
                const selectedOption = selector.options[selector.selectedIndex];
                imageSrc = selectedOption?.dataset.imageSrc || '';
                imageAlt = selectedOption?.dataset.imageAlt || '';
            } else {
                imageSrc = selector.dataset.imageSrc || '';
                imageAlt = selector.dataset.imageAlt || '';
            }

            if (!imageSrc) return;

            imageEl.setAttribute('src', imageSrc);
            if (imageAlt) {
                imageEl.setAttribute('alt', imageAlt);
            }
        }

        _updateItemImages() {
            document.querySelectorAll(SELECTORS.mandatoryItem + ', ' + SELECTORS.optionalItem).forEach((card) => {
                this._updateItemImage(card);
            });
        }

        _updateItemLink(card) {
            const variantId = this._getSelectedVariantId(card);
            if (!variantId) return;

            card.querySelectorAll('.bundle-set__item-title').forEach((link) => {
                const currentHref = link.getAttribute('href');
                if (!currentHref) return;

                if (!link.dataset.baseHref) {
                    link.dataset.baseHref = currentHref;
                }

                const baseHref = link.dataset.baseHref;

                try {
                    const url = new URL(baseHref, window.location.origin);
                    url.searchParams.set('variant', variantId);
                    link.setAttribute('href', url.pathname + url.search);
                } catch (error) {
                    const cleanBaseHref = baseHref.split('?')[0];
                    link.setAttribute('href', cleanBaseHref + '?variant=' + variantId);
                }
            });
        }

        _updateItemLinks() {
            document.querySelectorAll(SELECTORS.mandatoryItem + ', ' + SELECTORS.optionalItem).forEach((card) => {
                this._updateItemLink(card);
            });
        }

        _syncMainGalleryFromPrimaryItem() {
            const primaryCard = document.querySelector(SELECTORS.mandatoryItem) || document.querySelector(SELECTORS.optionalItem);
            if (primaryCard) {
                this._syncMainGalleryFromItem(primaryCard);
            }
        }

        _syncMainGalleryFromPrimaryItemWithRetry(attempt = 0) {
            const maxAttempts = 12;
            const retryDelayMs = 120;

            const productMedia = document.querySelector('product-media');
            const isReady = productMedia && productMedia.product && Array.isArray(productMedia.product.media);

            if (isReady) {
                this._syncMainGalleryFromPrimaryItem();
                return;
            }

            if (attempt >= maxAttempts) {
                return;
            }

            setTimeout(() => this._syncMainGalleryFromPrimaryItemWithRetry(attempt + 1), retryDelayMs);
        }

        _buildVariantOptionMapFromCard(card) {
            const selector = card.querySelector('select' + SELECTORS.variantSelector);
            if (!selector) return null;

            const selectedOption = selector.options[selector.selectedIndex];
            if (!selectedOption) return null;

            let optionNames = [];
            let optionValues = [];

            try {
                optionNames = JSON.parse(selector.dataset.optionNames || '[]');
            } catch (error) {
                optionNames = [];
            }

            try {
                optionValues = JSON.parse(selectedOption.dataset.variantOptions || '[]');
            } catch (error) {
                optionValues = [];
            }

            if (!Array.isArray(optionNames) || !Array.isArray(optionValues) || optionNames.length === 0) {
                return null;
            }

            const map = {};
            optionNames.forEach((name, index) => {
                const value = optionValues[index];
                if (typeof name === 'string' && typeof value === 'string') {
                    map[name.trim().toLowerCase()] = value.trim().toLowerCase();
                }
            });

            return Object.keys(map).length ? map : null;
        }

        _syncMainGalleryFromItem(card) {
            const optionMap = this._buildVariantOptionMapFromCard(card);
            if (!optionMap) return;

            const productMedia = document.querySelector('product-media');
            if (!productMedia || !productMedia.product || !Array.isArray(productMedia.product.media)) {
                return;
            }

            const filteredMediaIds = [];
            let hasTaggableMedia = false;

            productMedia.product.media.forEach((media) => {
                if (!media || !media.alt || !media.alt.includes('#')) return;

                hasTaggableMedia = true;
                const tags = media.alt.split('#').slice(1);
                let matches = false;

                tags.forEach((tag) => {
                    const normalized = tag.trim();
                    if (!normalized.includes('_')) return;

                    const tagParts = normalized.split('_');
                    const optionName = tagParts.shift().trim().toLowerCase();
                    const optionValue = tagParts.join('_').trim().toLowerCase();

                    if (optionMap[optionName] && optionMap[optionName] === optionValue) {
                        matches = true;
                    }
                });

                if (!matches) {
                    filteredMediaIds.push(media.id);
                }
            });

            if (!hasTaggableMedia) return;

            let selectedMediaId = productMedia.selectedVariantMediaId;
            if (!selectedMediaId || filteredMediaIds.includes(selectedMediaId)) {
                const firstVisibleMedia = productMedia.product.media.find((media) => !filteredMediaIds.includes(media.id));
                selectedMediaId = firstVisibleMedia ? firstVisibleMedia.id : null;
            }

            Array.from(document.querySelectorAll('[data-media-id]')).forEach((node) => {
                const mediaId = parseInt(node.getAttribute('data-media-id') || '', 10);
                if (!Number.isInteger(mediaId)) return;

                const isFiltered = filteredMediaIds.includes(mediaId);
                node.classList.toggle('is-filtered', isFiltered);

                if (node.classList.contains('product__media-item')) {
                    const isSelected = selectedMediaId === mediaId;
                    node.classList.toggle('is-selected', isSelected);
                    node.classList.toggle('is-initial-selected', isSelected);
                }

                if (node.hasAttribute('aria-current')) {
                    if (selectedMediaId === mediaId) {
                        node.setAttribute('aria-current', 'true');
                    } else {
                        node.removeAttribute('aria-current');
                    }
                }
            });

            if (productMedia.mainCarousel && typeof productMedia.mainCarousel.reload === 'function') {
                productMedia.mainCarousel.reload();
            }

            if (productMedia.mainCarousel && selectedMediaId && typeof productMedia.mainCarousel.select === 'function') {
                productMedia.mainCarousel.select('[data-media-id="' + selectedMediaId + '"]');
            }

            productMedia.selectedVariantMediaId = selectedMediaId;
        }

        _getSelectedVariantId(card) {
            const selector = card.querySelector(SELECTORS.variantSelector);
            if (!selector) return null;
            return parseInt(selector.value, 10);
        }

        _getSelectedVariantPrice(card) {
            const selector = card.querySelector(SELECTORS.variantSelector);
            if (!selector) return 0;

            if (selector.tagName === 'SELECT') {
                const option = selector.options[selector.selectedIndex];
                return parseInt(option?.dataset.price || '0', 10);
            }

            // Hidden input (single-variant product)
            return parseInt(selector.dataset.price || '0', 10);
        }

        _getOptionalItemQuantity(card) {
            const input = card.querySelector(SELECTORS.optionalQty);
            return Math.max(0, parseInt(input?.value || '0', 10) || 0);
        }

        _getSelectedVariantComparePrice(card) {
            const selector = card.querySelector(SELECTORS.variantSelector);
            if (!selector) return 0;

            if (selector.tagName === 'SELECT') {
                const option = selector.options[selector.selectedIndex];
                return parseInt(option?.dataset.comparePrice || '0', 10);
            }

            // Hidden input (single-variant product)
            return parseInt(selector.dataset.comparePrice || '0', 10);
        }

        _updateTotal() {
            let total = 0;
            let compareTotal = 0;

            // Sum all mandatory items (unit price × item quantity)
            document.querySelectorAll(SELECTORS.mandatoryItem).forEach((card) => {
                const qtyBadge = card.querySelector('[data-item-qty]');
                const itemQty = qtyBadge ? parseInt(qtyBadge.dataset.itemQty, 10) : 1;
                const price = this._getSelectedVariantPrice(card);
                const comparePrice = this._getSelectedVariantComparePrice(card);

                total += price * itemQty;
                compareTotal += Math.max(comparePrice, price) * itemQty;
            });

            // Multiply by bundle quantity
            const bundleQty = this.bundleQtyInput ? parseInt(this.bundleQtyInput.value, 10) || 1 : 1;
            total = total * bundleQty;
            compareTotal = compareTotal * bundleQty;

            // Add optional items with a selected quantity (not multiplied by bundle qty)
            document.querySelectorAll(SELECTORS.optionalItem).forEach((card) => {
                const itemQty = this._getOptionalItemQuantity(card);
                if (itemQty < 1) return;
                const price = this._getSelectedVariantPrice(card);
                const comparePrice = this._getSelectedVariantComparePrice(card);

                total += price * itemQty;
                compareTotal += Math.max(comparePrice, price) * itemQty;
            });

            if (this.totalEl) {
                this.totalEl.textContent = formatMoney(total);
                this.totalEl.classList.toggle('bundle-set__running-total--sale', compareTotal > total);
            }

            if (this.totalCompareEl) {
                if (compareTotal > total) {
                    this.totalCompareEl.textContent = formatMoney(compareTotal);
                    this.totalCompareEl.hidden = false;
                } else {
                    this.totalCompareEl.textContent = '';
                    this.totalCompareEl.hidden = true;
                }
            }
        }

        _buildCartItems() {
            const items = [];
            const bundleQty = this.bundleQtyInput ? parseInt(this.bundleQtyInput.value, 10) || 1 : 1;
            const bundleProperties = {
                _bundle_id: this.data.bundleHandle,
                _bundle_source: 'bundle-set',
            };

            // Build cart items for all mandatory items
            document.querySelectorAll(SELECTORS.mandatoryItem).forEach((card) => {
                const variantId = this._getSelectedVariantId(card);
                const qtyBadge = card.querySelector('[data-item-qty]');
                const itemQty = qtyBadge ? parseInt(qtyBadge.dataset.itemQty, 10) : 1;

                if (variantId) {
                    items.push({
                        id: variantId,
                        quantity: itemQty * bundleQty,  // Multiply by bundle quantity
                        properties: bundleProperties,
                    });
                }
            });

            // Add optional items with a selected quantity (not multiplied by bundle qty)
            document.querySelectorAll(SELECTORS.optionalItem).forEach((card) => {
                const variantId = this._getSelectedVariantId(card);
                const itemQty = this._getOptionalItemQuantity(card);

                if (variantId && itemQty > 0) {
                    items.push({
                        id: variantId,
                        quantity: itemQty,
                        properties: bundleProperties,
                    });
                }
            });

            return items;
        }

        _validate(items) {
            if (items.length === 0) return false;

            // Check that all multi-variant items have valid (non-disabled) variants selected
            let valid = true;

            document.querySelectorAll(SELECTORS.mandatoryItem).forEach((card) => {
                const selector = card.querySelector('select' + SELECTORS.variantSelector);
                if (!selector) {
                    // Single-variant product: valid only if hidden input is present.
                    // No hidden input means first_available_variant was nil (variant
                    // unpublished on Online Store channel — Shopify Spring 2026).
                    if (!card.querySelector('input' + SELECTORS.variantSelector)) {
                        valid = false;
                    }
                    return;
                }

                const selected = selector.options[selector.selectedIndex];
                if (selected?.disabled) {
                    valid = false;
                }
            });

            document.querySelectorAll(SELECTORS.optionalItem).forEach((card) => {
                if (this._getOptionalItemQuantity(card) < 1) return;

                const selector = card.querySelector('select' + SELECTORS.variantSelector);
                if (!selector) return; // Single-variant product, always valid

                const selected = selector.options[selector.selectedIndex];
                if (selected?.disabled) {
                    valid = false;
                }
            });

            return valid;
        }

        async _onCheckout() {
            this._clearError();
            const items = this._buildCartItems();

            if (!this._validate(items)) {
                this._showError('Please select valid options for all items.');
                return;
            }

            this.checkoutButton.disabled = true;
            const originalText = this.checkoutButton.textContent;
            this.checkoutButton.textContent = 'Processing…';

            try {
                await addItemsToCart(items);
                window.location.href = '/checkout';
            } catch (err) {
                this._showError(err.message);
                this.checkoutButton.disabled = false;
                this.checkoutButton.textContent = originalText;
            }
        }

        async _onAddToCart() {
            this._clearError();
            const items = this._buildCartItems();

            if (!this._validate(items)) {
                this._showError('Please select valid options for all items.');
                return;
            }

            this.atcButton.disabled = true;
            const originalText = this.atcButton.textContent;
            this.atcButton.textContent = 'Adding…';

            try {
                const result = await addItemsToCart(items);
                await notifyCartUpdated(result);
            } catch (err) {
                this._showError(err.message);
            } finally {
                this.atcButton.disabled = false;
                this.atcButton.textContent = originalText;
            }
        }

        _showError(message) {
            if (this.errorEl) {
                this.errorEl.textContent = message;
                this.errorEl.hidden = false;
            }
        }

        _clearError() {
            if (this.errorEl) {
                this.errorEl.textContent = '';
                this.errorEl.hidden = true;
            }
        }
    }

    // ─── Init ─────────────────────────────────────────────────────────────────

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => new BundleSet());
    } else {
        new BundleSet();
    }

})();
